import { useState, useCallback, useRef } from 'react'
import { getCookie } from '../utils/helpers.js'
import { TOKEN, getApiBaseUrl } from '@constants/constants'

export const useWorkforce = (initialFilters, initialPagination) => {
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [pagination, setPagination] = useState(initialPagination)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filters, setFilters] = useState(initialFilters)
  const [debouncedFilters, setDebouncedFilters] = useState(initialFilters)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [selectedTeamType, setSelectedTeamType] = useState(null)
  const [fixedTotalEmployeeCount, setFixedTotalEmployeeCount] = useState(0)
  const [weeklyAvgUtilization, setWeeklyAvgUtilization] = useState(null)
  const [weeklyUtilizationLoading, setWeeklyUtilizationLoading] = useState(false)
  const [weeklyUtilizationError, setWeeklyUtilizationError] = useState(null)
  
  const abortControllerRef = useRef(null)

  const fetchEmployees = useCallback(async (
    offset = 0, 
    limit = 100, 
    search = '', 
    filterValues = debouncedFilters,
    append = false,
    teamFilter = null,
    sortKey = null,
    sortDirection = 'asc'
  ) => {
    if (abortControllerRef.current) abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()
    
    if (append) setLoadingMore(true); else setLoading(true)
    setFetchError(null)
    
    try {
      const token = getCookie(TOKEN)
      if (!token) return null
      
      const apiBaseUrl = getApiBaseUrl()
      const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() })
      if (append && offset > 0) params.append('skip_count', 'true')
      if (search && search.trim()) params.append('search', search.trim())
      
      if (filterValues.parentDepartment?.length > 0) params.append('parent_department', filterValues.parentDepartment.join(','))
      if (filterValues.department?.length > 0) params.append('department', filterValues.department.join(','))
      if (filterValues.location?.length > 0) params.append('location', filterValues.location.join(','))
      if (filterValues.role?.length > 0) params.append('designation', filterValues.role.join(','))
      if (filterValues.status?.length > 0) params.append('status', filterValues.status.join(','))
      if (filterValues.experience?.length > 0) params.append('experience', filterValues.experience.join(','))
      if (filterValues.project?.length > 0) params.append('project', filterValues.project.join(','))
      if (teamFilter) params.append('team_filter', teamFilter)
      if (sortKey) { params.append('sort_by', sortKey); params.append('sort_direction', sortDirection) }
      
      const response = await fetch(`${apiBaseUrl}/api/employees?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: abortControllerRef.current.signal
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      const data = await response.json()
      const fetchedEmployees = data.employees || []
      const hasMore = data.has_more !== undefined ? data.has_more : fetchedEmployees.length === limit
      const totalCount = data.total_count || data.totalCount || fetchedEmployees.length

      if (append) {
        setEmployees(prev => [...prev, ...fetchedEmployees])
      } else {
        setEmployees(fetchedEmployees)
      }
      
      const newOffset = offset + fetchedEmployees.length
      setPagination(prev => ({ 
        ...prev, 
        offset: newOffset, 
        hasMore, 
        totalCount: append ? prev.totalCount : totalCount 
      }))
      
      setLoading(false); setLoadingMore(false); setInitialLoadComplete(true)
      return { employees: fetchedEmployees, hasMore, totalCount }
    } catch (error) {
      if (error.name === 'AbortError') return null
      setFetchError(error.message || 'Failed to load employees.')
      setLoading(false); setLoadingMore(false); setInitialLoadComplete(true)
      return null
    }
  }, [debouncedFilters])

  const fetchFixedTotalEmployeeCount = useCallback(async () => {
    const token = getCookie(TOKEN); if (!token) return
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/employees/count`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (res.ok) { const data = await res.json(); setFixedTotalEmployeeCount(data.count ?? 0) }
    } catch (err) { console.error(err) }
  }, [])

  const fetchWeeklyAvgUtilization = useCallback(async () => {
    const token = getCookie(TOKEN); if (!token) return
    setWeeklyUtilizationLoading(true); setWeeklyUtilizationError(null)
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/employees/utilization/weekly-average`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (res.ok) { const data = await res.json(); setWeeklyAvgUtilization(data) }
      else setWeeklyUtilizationError('Failed to load')
    } catch (err) { setWeeklyUtilizationError('Failed to load') }
    finally { setWeeklyUtilizationLoading(false) }
  }, [])

  const handleSort = useCallback((key) => {
    setSortConfig(prev => {
      const direction = (prev.key === key && prev.direction === 'asc') ? 'desc' : 'asc'
      return { key, direction }
    })
    setPagination(prev => ({ ...prev, offset: 0, hasMore: true }))
  }, [])

  const clearFilters = useCallback(() => {
    setSearchTerm(''); setDebouncedSearchTerm('')
    setFilters(initialFilters); setDebouncedFilters(initialFilters)
    setSortConfig({ key: null, direction: 'asc' }); setSelectedTeamType(null)
    setPagination(prev => ({ ...prev, offset: 0, hasMore: true }))
  }, [initialFilters])

  return {
    employees, setEmployees, loading, loadingMore, initialLoadComplete, fetchError, setFetchError, pagination, setPagination,
    searchTerm, setSearchTerm, debouncedSearchTerm, setDebouncedSearchTerm, filters, setFilters, debouncedFilters, setDebouncedFilters,
    sortConfig, handleSort, selectedTeamType, setSelectedTeamType, fixedTotalEmployeeCount, fetchFixedTotalEmployeeCount,
    weeklyAvgUtilization, weeklyUtilizationLoading, weeklyUtilizationError, fetchWeeklyAvgUtilization,
    fetchEmployees, clearFilters
  }
}
